import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import './App.css';

function Navbar() {
    const location = useLocation();
    const [scrolled, setScrolled] = useState(false);
    const [isMobile, setIsMobile] = useState(window.innerWidth < 992);

    const primaryOrange = "#ff8e18";

    useEffect(() => {
        const handleScroll = () => {
            setScrolled(window.scrollY > 20);
        };
        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    useEffect(() => {
        const handleResize = () => {
            setIsMobile(window.innerWidth < 992);
        };
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const navLinks = [
        { path: '/', label: 'Showroom', icon: 'bi-grid-fill', mobileIcon: 'bi-house-door-fill' },
        { path: '/garantie', label: 'Garanție', icon: 'bi-shield-check', mobileIcon: 'bi-shield-check' },
        { path: '/livrare', label: 'Livrare', icon: 'bi-truck', mobileIcon: 'bi-truck' },
        { path: '/contact', label: 'Contact', icon: 'bi-geo-alt-fill', mobileIcon: 'bi-envelope' }
    ];

    // ── Mobile: sticky bottom pill (floats while scrolling, docks above footer) ──
    if (isMobile) {
        return (
            <nav className="mnav" aria-label="Navigare principală">
                {navLinks.map((item) => {
                    const isActive = location.pathname === item.path;
                    return (
                        <Link
                            key={item.path}
                            to={item.path}
                            className={`mnav-tab${isActive ? ' mnav-tab--active' : ''}`}
                            aria-label={item.label}
                            aria-current={isActive ? 'page' : undefined}
                        >
                            <i className={`bi ${item.mobileIcon}`} />
                        </Link>
                    );
                })}

                <a href="tel:0741811511" className="mnav-call" aria-label="Sună acum">
                    <i className="bi bi-telephone-fill" />
                </a>
            </nav>
        );
    }

    // ── Desktop: sticky top glassmorphism ───────────────────────────────────
    return (
        <nav
            className="navbar navbar-expand-lg navbar-dark fixed-top"
            style={{
                background: (scrolled || location.pathname.startsWith('/masina/')) ? 'rgba(28,46,74,0.92)' : 'rgba(28,46,74,0.60)',
                backdropFilter: 'blur(12px)',
                WebkitBackdropFilter: 'blur(12px)',
                borderBottom: '1px solid rgba(255,255,255,0.07)',
                boxShadow: (scrolled || location.pathname.startsWith('/masina/')) ? '0 4px 30px rgba(0,0,0,0.25)' : 'none',
                transition: 'all 0.3s ease',
                zIndex: 9999,
                padding: '10px 0'
            }}
        >
            <div className="container">
                {/* Logo */}
                <Link className="navbar-brand d-flex align-items-center py-0" to="/">
                    <img
                        src="/cheapcarslogo.png"
                        alt="SPCheapCars"
                        onError={(e) => {
                            e.target.onerror = null;
                            e.target.style.display = 'none';
                            const fallback = document.createElement('span');
                            fallback.style.cssText = 'color:#ffffff;font-weight:800;font-size:1.4rem;letter-spacing:0.5px';
                            fallback.innerHTML = '<span style="color:#ff8e18">SP</span>CheapCars';
                            e.target.parentElement.appendChild(fallback);
                        }}
                        style={{
                            height: '60px',
                            width: 'auto',
                            objectFit: 'contain',
                            transform: 'scale(1.25)',
                            transformOrigin: 'left center'
                        }}
                    />
                </Link>

                {/* Desktop nav */}
                <div className="collapse navbar-collapse" id="navbarContent">
                    <ul className="navbar-nav mx-auto mb-2 mb-lg-0">
                        {navLinks.map((item) => (
                            <li className="nav-item mx-1" key={item.path}>
                                <Link
                                    className="nav-link px-3 py-2 d-flex align-items-center gap-2"
                                    to={item.path}
                                    style={{
                                        color: location.pathname === item.path ? '#ffffff' : 'rgba(255,255,255,0.80)',
                                        fontWeight: '600',
                                        fontSize: '0.85rem',
                                        textTransform: 'uppercase',
                                        letterSpacing: '0.5px',
                                        borderRadius: '8px',
                                        background: 'transparent',
                                        transition: 'all 0.2s ease',
                                        position: 'relative'
                                    }}
                                    onMouseEnter={(e) => {
                                        if (location.pathname !== item.path) {
                                            e.currentTarget.style.color = primaryOrange;
                                        }
                                    }}
                                    onMouseLeave={(e) => {
                                        if (location.pathname !== item.path) {
                                            e.currentTarget.style.color = 'rgba(255,255,255,0.80)';
                                        }
                                    }}
                                >
                                    <i className={`bi ${item.icon}`} style={{
                                        fontSize: '0.75rem',
                                        color: location.pathname === item.path ? primaryOrange : 'inherit'
                                    }}></i>
                                    {item.label}
                                    {location.pathname === item.path && (
                                        <span style={{
                                            position: 'absolute',
                                            bottom: '4px',
                                            left: '50%',
                                            transform: 'translateX(-50%)',
                                            width: '20px',
                                            height: '2px',
                                            background: primaryOrange,
                                            borderRadius: '2px'
                                        }} />
                                    )}
                                </Link>
                            </li>
                        ))}
                    </ul>

                    {/* Desktop call button */}
                    <a
                        href="tel:0741811511"
                        className="text-decoration-none d-flex align-items-center gap-2"
                        style={{
                            padding: '10px 22px',
                            borderRadius: '50px',
                            background: `linear-gradient(135deg, ${primaryOrange} 0%, #e67e00 100%)`,
                            border: 'none',
                            color: '#ffffff',
                            fontWeight: '700',
                            fontSize: '0.85rem',
                            letterSpacing: '0.3px',
                            boxShadow: `0 4px 18px ${primaryOrange}55`,
                            transition: 'all 0.2s ease',
                            cursor: 'pointer'
                        }}
                        onMouseEnter={(e) => {
                            e.currentTarget.style.transform = 'translateY(-1px)';
                            e.currentTarget.style.boxShadow = `0 8px 24px ${primaryOrange}70`;
                        }}
                        onMouseLeave={(e) => {
                            e.currentTarget.style.transform = 'translateY(0)';
                            e.currentTarget.style.boxShadow = `0 4px 18px ${primaryOrange}55`;
                        }}
                    >
                        <i className="bi bi-telephone-fill" style={{ color: '#ffffff', fontSize: '0.8rem' }}></i>
                        <span>0741 811 511</span>
                    </a>
                </div>
            </div>
        </nav>
    );
}

export default Navbar;
